import { useState, useEffect } from 'react';
import { Star, DollarSign, Percent, Calendar, ExternalLink, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { buyerApi, StarredOffer } from '@/services/api/buyer.api';
import { useLanguage } from '@/contexts/LanguageContext';

export function StarredOffersPanel() {
  const [offers, setOffers] = useState<StarredOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { toast } = useToast();
  const { language } = useLanguage();

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      const data = await buyerApi.getStarredOffers();
      setOffers(data);
    } catch (error) {
      console.error('Failed to load starred offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (offerId: string) => {
    setRemovingId(offerId);
    try {
      await buyerApi.unstarOffer(offerId);
      setOffers((prev) => prev.filter((o) => o.offerId !== offerId));
      toast({
        title: language === 'fr' ? 'Offre retirée' : 'Offer removed',
      });
    } catch (error) {
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        variant: 'destructive',
      });
    } finally {
      setRemovingId(null);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Star className="h-5 w-5 text-primary fill-primary" />
          {language === 'fr' ? 'Offres de financement sauvegardées' : 'Saved Financing Offers'}
          {offers.length > 0 && (
            <Badge variant="secondary" className="ml-2">{offers.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {offers.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-8">
            {language === 'fr'
              ? 'Aucune offre sauvegardée. Cliquez sur l\'étoile ⭐ sur les offres du deal monitor pour les sauvegarder ici.'
              : 'No saved offers. Click the star ⭐ on deal monitor offers to save them here.'}
          </p>
        ) : (
          <div className="space-y-3">
            {offers.map((starred) => {
              const snap = starred.offerSnapshot;
              const termLabel = snap.termMonths
                ? `${snap.termMonths} ${language === 'fr' ? 'mois' : 'months'}`
                : '—';

              return (
                <div
                  key={starred.id}
                  className="flex items-start justify-between gap-3 p-4 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 space-y-2">
                    {snap.dealName && (
                      <p className="text-sm font-medium text-foreground">{snap.dealName}</p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3.5 w-3.5 text-primary" />
                        <span className="font-semibold">{formatCurrency(snap.loanAmount)}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Percent className="h-3.5 w-3.5 text-primary" />
                        {snap.interestRate}%
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                        {termLabel}
                      </span>
                    </div>

                    {snap.loanType && (
                      <Badge variant="secondary" className="text-xs">{snap.loanType}</Badge>
                    )}

                    {/* BYOD: show lender info */}
                    {snap.isByod && !snap.isAnonymous && snap.lenderName && (
                      <div className="pt-1">
                        <p className="text-xs font-medium">{snap.lenderName}</p>
                        {snap.lenderCompany && (
                          <p className="text-xs text-muted-foreground">{snap.lenderCompany}</p>
                        )}
                        {snap.calendarLink && (
                          <a
                            href={snap.calendarLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {language === 'fr' ? 'Prendre rendez-vous' : 'Book a meeting'}
                          </a>
                        )}
                      </div>
                    )}

                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(starred.offerId)}
                    disabled={removingId === starred.offerId}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                  >
                    {removingId === starred.offerId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
